/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type CreateStaffUserInput, type OnboardClinicInput } from '@therapy/shared-validators';

import { apiClient } from './client.js';

export type StaffRole = 'PLATFORM_OWNER' | 'CUSTOMER_SUCCESS' | 'SUPPORT' | 'READONLY_AUDITOR';

/** A clinic's lifecycle status as surfaced by the Super Admin console. */
export type ClinicStatus = string;

/** RAG health buckets across all tenant clinics. */
export interface HealthBuckets {
  readonly red: number;
  readonly amber: number;
  readonly green: number;
}

export interface TopBySessions {
  readonly clinicId: string;
  readonly name: string;
  readonly sessions: number;
}

export interface TopByRevenue {
  readonly clinicId: string;
  readonly name: string;
  readonly revenuePaise: number;
}

/** `GET /sa/dashboard` — platform-wide KPIs for the staff console. */
export interface SaDashboard {
  readonly totalClinics: number;
  readonly activeClinics: number;
  readonly mrrPaise: number;
  readonly health: HealthBuckets;
  readonly topBySessions: readonly TopBySessions[];
  readonly topByRevenue: readonly TopByRevenue[];
}

/** One row of `GET /sa/clinics`. */
export interface ClinicListItem {
  readonly id: string;
  readonly name: string;
  readonly status: ClinicStatus;
  readonly planName: string | null;
  readonly mrrPaise: number;
  readonly therapistCount: number;
  readonly activeClientCount: number;
  readonly createdAt: string;
}

export interface ClinicSubscription {
  readonly planName: string | null;
  readonly status: string | null;
  readonly currentPeriodEnd: string | null;
  readonly mrrPaise: number;
}

/** One row of `GET /sa/subscriptions`. */
export interface SaSubscription {
  readonly clinicId: string;
  readonly clinicName: string;
  readonly planName: string | null;
  readonly status: string;
  readonly mrrPaise: number;
  readonly trialEndsAt: string | null;
  readonly currentPeriodEnd: string | null;
}

/** One row of `GET /sa/staff-users`. */
export interface SaStaffUser {
  readonly id: string;
  readonly email: string;
  readonly fullName: string;
  readonly active: boolean;
  readonly roles: readonly StaffRole[];
  readonly lastLoginAt: string | null;
  readonly totpConfirmed: boolean;
}

/** `POST /sa/staff-users` — the one-time temp password is returned once. */
export interface CreatedStaffUser {
  readonly id: string;
  readonly email: string;
  readonly tempPassword: string;
}

/** One row of `GET /sa/usage`. */
export interface SaUsage {
  readonly clinicId: string;
  readonly clinicName: string;
  readonly health: string;
  readonly sessions30d: number;
  readonly whatsapp30d: number;
  readonly sms30d: number;
  readonly pdfs30d: number;
  readonly storageMb: number;
  readonly activeUsers: number;
}

export interface ClinicCounts {
  readonly therapists: number;
  readonly activeClients: number;
  readonly sessions: number;
  readonly invoicedPaise: number;
}

export interface ClinicAuditEntry {
  readonly at: string;
  readonly action: string;
  readonly actorType: string;
}

/** `GET /sa/clinics/:id` — full clinic detail for the staff console. */
export interface ClinicDetail {
  readonly id: string;
  readonly name: string;
  readonly legalName: string | null;
  readonly status: ClinicStatus;
  readonly contactEmail: string;
  readonly contactPhone: string;
  readonly timezone: string;
  readonly createdAt: string;
  readonly subscription: ClinicSubscription | null;
  readonly counts: ClinicCounts;
  readonly recentAudit: readonly ClinicAuditEntry[];
}

/** One row of `GET /sa/audit`. */
export interface AuditEntry {
  readonly at: string;
  readonly clinicId: string | null;
  readonly clinicName: string | null;
  readonly actorType: string;
  readonly actorId: string | null;
  readonly action: string;
  readonly entity: string | null;
  readonly entityId: string | null;
}

/** Query filters for the audit log endpoint (all optional). */
export interface AuditFilters {
  readonly clinicId?: string | undefined;
  readonly action?: string | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly limit?: number | undefined;
}

function auditParams(filters: AuditFilters): Record<string, string> {
  const params: Record<string, string> = {};
  if (filters.clinicId !== undefined && filters.clinicId !== '') {
    params.clinicId = filters.clinicId;
  }
  if (filters.action !== undefined && filters.action !== '') {
    params.action = filters.action;
  }
  if (filters.from !== undefined && filters.from !== '') {
    params.from = filters.from;
  }
  if (filters.to !== undefined && filters.to !== '') {
    params.to = filters.to;
  }
  if (filters.limit !== undefined) {
    params.limit = String(filters.limit);
  }
  return params;
}

export interface FeatureFlag {
  readonly key: string;
  readonly enabled: boolean;
  readonly updatedAt: string;
}

export const saApi = {
  async dashboard(): Promise<SaDashboard> {
    const { data } = await apiClient.get<SaDashboard>('/sa/dashboard');
    return data;
  },
  async clinics(): Promise<readonly ClinicListItem[]> {
    const { data } = await apiClient.get<readonly ClinicListItem[]>('/sa/clinics');
    return data;
  },
  async markSubscriptionPaid(clinicId: string): Promise<void> {
    await apiClient.post(`/sa/subscriptions/${clinicId}/mark-paid`);
  },
  async subscriptions(): Promise<readonly SaSubscription[]> {
    const { data } = await apiClient.get<readonly SaSubscription[]>('/sa/subscriptions');
    return data;
  },
  async usage(): Promise<readonly SaUsage[]> {
    const { data } = await apiClient.get<readonly SaUsage[]>('/sa/usage');
    return data;
  },
  async staffUsers(): Promise<readonly SaStaffUser[]> {
    const { data } = await apiClient.get<readonly SaStaffUser[]>('/sa/staff-users');
    return data;
  },
  async createStaffUser(payload: CreateStaffUserInput): Promise<CreatedStaffUser> {
    const { data } = await apiClient.post<CreatedStaffUser>('/sa/staff-users', payload);
    return data;
  },
  async setStaffActive(id: string, active: boolean): Promise<SaStaffUser> {
    const { data } = await apiClient.patch<SaStaffUser>(`/sa/staff-users/${id}`, { active });
    return data;
  },
  async clinic(id: string): Promise<ClinicDetail> {
    const { data } = await apiClient.get<ClinicDetail>(`/sa/clinics/${id}`);
    return data;
  },
  async audit(filters: AuditFilters): Promise<readonly AuditEntry[]> {
    const { data } = await apiClient.get<readonly AuditEntry[]>('/sa/audit', {
      params: auditParams(filters),
    });
    return data;
  },
  async onboard(payload: OnboardClinicInput): Promise<ClinicDetail> {
    const { data } = await apiClient.post<ClinicDetail>('/sa/clinics/onboard', payload);
    return data;
  },
  async featureFlags(): Promise<readonly FeatureFlag[]> {
    const { data } = await apiClient.get<readonly FeatureFlag[]>('/sa/feature-flags');
    return data;
  },
  async setFeatureFlag(key: string, enabled: boolean): Promise<FeatureFlag> {
    const { data } = await apiClient.patch<FeatureFlag>(
      `/sa/feature-flags/${encodeURIComponent(key)}`,
      { enabled },
    );
    return data;
  },
};
